import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Flame, Bookmark, Calendar, NotebookPen, Sparkles,
  TrendingUp, Trophy, ChevronRight, Share2, Sprout, TreePine,
  BookOpen, Target, PenLine,
} from "lucide-react";
import {
  getCurrentStreak, getLongestStreak, getCompletionsCount,
  getCompletedPlansCount, getAllFavorites, getWeekCompletion,
} from "../lib/dailyJourney";
import { journalApi } from "../lib/journal";
import { useAuth } from "../lib/authContext";
import { assistantApi } from "../lib/assistantApi";
import ShareStreakModal from "../components/ShareStreakModal";
import ShareAppModal from "../components/ShareAppModal";
import PushOptInCard from "../components/PushOptInCard";
import JourneyToday from "../components/JourneyToday";
import JourneyGoals from "../components/JourneyGoals";
import MyImpactCard from "../features/share/MyImpactCard";
import StreakVisual from "../components/StreakVisual";

/* -------------------------- Achievements -------------------------- */
/**
 * Achievements are computed deterministically from local progress + cloud
 * data when authenticated. Each one is unlocked when its condition is met
 * (no extra storage needed).
 */
const ACHIEVEMENTS = [
  { id: "first-day", Icon: Sprout, title: "Primeira semente", desc: "1º dia de devocional concluído", needs: (s) => s.totalDays >= 1 },
  { id: "week-streak", Icon: Flame, title: "Chama acesa", desc: "7 dias seguidos com Deus", needs: (s) => s.streak >= 7 },
  { id: "month-streak", Icon: TreePine, title: "Raízes profundas", desc: "30 dias seguidos", needs: (s) => s.longestStreak >= 30 },
  { id: "first-fav", Icon: Bookmark, title: "Versículo ancorado", desc: "1 versículo favoritado", needs: (s) => s.favCount >= 1 },
  { id: "ten-favs", Icon: BookOpen, title: "Coleção viva", desc: "10 versículos favoritados", needs: (s) => s.favCount >= 10 },
  { id: "first-plan", Icon: Target, title: "Plano completo", desc: "1 plano de leitura finalizado", needs: (s) => s.completedPlans >= 1 },
  { id: "first-journal", Icon: PenLine, title: "Diário aberto", desc: "1ª reflexão escrita", needs: (s) => s.journalCount >= 1 },
  { id: "ten-journal", Icon: NotebookPen, title: "Caminho registrado", desc: "10 reflexões no Diário", needs: (s) => s.journalCount >= 10 },
  { id: "first-ai", Icon: Sparkles, title: "Conversando com sabedoria", desc: "1ª pergunta ao Auxiliar Bíblico", needs: (s) => s.aiAsked >= 1 },
];

/* ------------------------------ UI -------------------------------- */
const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div
    className="bg-white border border-border rounded-2xl p-5"
    data-testid={testid}
  >
    <div className="flex items-center gap-2.5 mb-2">
      <div className="w-9 h-9 rounded-xl bg-[hsl(var(--parchment))] flex items-center justify-center">
        <Icon className="w-4 h-4 text-gold" />
      </div>
      <p className="text-[10px] uppercase tracking-[0.3em] text-navy/55">{label}</p>
    </div>
    <p className="font-display text-3xl text-navy leading-none">{value}</p>
    {sub && <p className="text-[12px] text-navy/55 mt-1.5">{sub}</p>}
  </div>
);

export default function Jornada() {
  const { isAuthenticated, user } = useAuth();
  const [shareOpen, setShareOpen] = useState(false);
  const [shareAppOpen, setShareAppOpen] = useState(false);

  const [stats, setStats] = useState(() => ({
    streak: getCurrentStreak(),
    longestStreak: getLongestStreak(),
    totalDays: getCompletionsCount(),
    completedPlans: getCompletedPlansCount(),
    favorites: getAllFavorites(),
    journalCount: 0,
    aiAsked: 0,
  }));
  const [loading, setLoading] = useState(false);

  // Local stats are read synchronously above. When authenticated, also pull
  // journal count + assistant usage from the server.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([journalApi.list(), assistantApi.usage()])
      .then(([journal, usage]) => {
        if (cancelled) return;
        const journalCount =
          journal.status === "fulfilled" && Array.isArray(journal.value)
            ? journal.value.length
            : 0;
        const aiAsked =
          usage.status === "fulfilled" && usage.value
            ? usage.value.used_today || 0
            : 0;
        setStats((s) => ({ ...s, journalCount, aiAsked }));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const favCount =
    stats.favorites.devotionals.length +
    stats.favorites.prayers.length +
    stats.favorites.passages.length;

  const computedStats = { ...stats, favCount };

  const unlocked = useMemo(
    () => ACHIEVEMENTS.filter((a) => a.needs(computedStats)),
    [computedStats],
  );
  const locked = useMemo(
    () => ACHIEVEMENTS.filter((a) => !a.needs(computedStats)),
    [computedStats],
  );

  const greeting = useMemo(() => {
    if (stats.streak >= 7) return "Sua constância é bênção.";
    if (stats.streak >= 1) return "Continue caminhando, um dia de cada vez.";
    if (stats.totalDays > 0) return "Toda jornada espiritual se renova em graça.";
    return "Sua jornada começa hoje.";
  }, [stats]);

  return (
    <div className="max-w-3xl mx-auto section-padding py-8" data-testid="jornada-page">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-navy mb-6"
        data-testid="jornada-back"
      >
        <ArrowLeft className="w-4 h-4" /> Início
      </Link>

      <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Sua jornada</p>
      <h1 className="font-display text-4xl sm:text-5xl text-navy mt-2">
        {isAuthenticated ? `Olá, ${user?.name || "amigo(a)"}` : "Sua caminhada"}
      </h1>
      <p className="text-navy/65 mt-3 text-sm">{greeting}</p>

      {/* Streak hero — StreakVisual unificado */}
      <section
        className="mt-6 bg-[#0D1B3E] border border-[#B8962E]/30 rounded-3xl p-6 sm:p-8 text-white relative overflow-hidden"
        data-testid="jornada-streak-hero"
      >
        <div className="absolute -right-10 -top-10 w-44 h-44 rounded-full bg-gold/15 blur-3xl pointer-events-none" />
        <div className="relative">
          <StreakVisual
            current={stats.streak}
            longest={stats.longestStreak}
            week={getWeekCompletion()}
            variant="dark"
            eyebrow="Ofensiva atual"
            testid="jornada-streak-visual"
            action={
              stats.streak > 0 ? (
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  data-testid="jornada-share-streak"
                  className="inline-flex items-center gap-2 bg-gold text-navy hover:bg-[#d4b876] px-5 py-2.5 rounded-full text-[11px] uppercase tracking-widest transition-colors"
                >
                  <Share2 className="w-3.5 h-3.5" />
                  Compartilhar minha ofensiva
                </button>
              ) : null
            }
          />
        </div>
      </section>

      <PushOptInCard />

      {/* FASE 2, Registro diário das 4 categorias (somente autenticados) */}
      {isAuthenticated && (
        <div className="mt-6 space-y-5">
          <JourneyToday />
          <JourneyGoals />
        </div>
      )}

      {/* Stats grid */}
      <section className="mt-5 grid grid-cols-2 gap-3" data-testid="jornada-stats">
        <StatCard
          icon={Calendar}
          label="Total de dias"
          value={stats.totalDays}
          sub="devocionais concluídos"
          testid="stat-total-days"
        />
        <StatCard
          icon={Bookmark}
          label="Favoritos"
          value={favCount}
          sub="versículos + devocionais"
          testid="stat-favorites"
        />
        <StatCard
          icon={TrendingUp}
          label="Planos finalizados"
          value={stats.completedPlans}
          sub="jornadas completas"
          testid="stat-plans"
        />
        <StatCard
          icon={NotebookPen}
          label="Diário"
          value={isAuthenticated ? stats.journalCount : ", "}
          sub={isAuthenticated ? "reflexões salvas" : "entre para ver"}
          testid="stat-journal"
        />
      </section>

      {/* Meu Impacto — só renderiza se logado e com ≥ 1 share */}
      <div className="mt-8" data-testid="jornada-impact-slot">
        <MyImpactCard />
      </div>

      {/* Achievements */}
      <section className="mt-8" data-testid="jornada-achievements">
        <div className="flex items-end justify-between mb-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-navy/55">Conquistas</p>
            <h2 className="font-display text-2xl text-navy mt-1">
              {unlocked.length}/{ACHIEVEMENTS.length} desbloqueadas
            </h2>
          </div>
          <Trophy className="w-5 h-5 text-gold" />
        </div>

        {unlocked.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-4" data-testid="achievements-unlocked">
            {unlocked.map((a) => (
              <div
                key={a.id}
                className="flex items-center gap-3 bg-[hsl(var(--parchment))] border border-gold/30 rounded-2xl px-4 py-3"
                data-testid={`achievement-${a.id}`}
              >
                <a.Icon className="w-6 h-6 text-gold shrink-0" />
                <div className="min-w-0">
                  <p className="font-display text-base text-navy leading-tight truncate">{a.title}</p>
                  <p className="text-[11px] text-navy/55 mt-0.5">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {locked.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="achievements-locked">
            {locked.map((a) => (
              <div
                key={a.id}
                className="flex items-center gap-3 bg-white border border-dashed border-border rounded-2xl px-4 py-3 opacity-60"
                data-testid={`achievement-locked-${a.id}`}
              >
                <a.Icon className="w-6 h-6 text-navy/35 shrink-0" />
                <div className="min-w-0">
                  <p className="font-display text-base text-navy/65 leading-tight truncate">{a.title}</p>
                  <p className="text-[11px] text-navy/45 mt-0.5">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Quick links to relevant sections */}
      <section className="mt-8 space-y-2" data-testid="jornada-quick-links">
        <Link
          to="/favoritos"
          className="flex items-center justify-between bg-white border border-border rounded-2xl px-5 py-4 hover:border-gold/40 transition-colors"
          data-testid="jornada-link-favoritos"
        >
          <div className="flex items-center gap-3">
            <Bookmark className="w-4 h-4 text-gold" />
            <p className="font-display text-base text-navy">Meus versículos</p>
          </div>
          <ChevronRight className="w-4 h-4 text-navy/40" />
        </Link>
        <Link
          to="/planos"
          className="flex items-center justify-between bg-white border border-border rounded-2xl px-5 py-4 hover:border-gold/40 transition-colors"
          data-testid="jornada-link-planos"
        >
          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-gold" />
            <p className="font-display text-base text-navy">Planos de leitura</p>
          </div>
          <ChevronRight className="w-4 h-4 text-navy/40" />
        </Link>
        <Link
          to="/diario"
          className="flex items-center justify-between bg-white border border-border rounded-2xl px-5 py-4 hover:border-gold/40 transition-colors"
          data-testid="jornada-link-diario"
        >
          <div className="flex items-center gap-3">
            <NotebookPen className="w-4 h-4 text-gold" />
            <p className="font-display text-base text-navy">Diário espiritual</p>
          </div>
          <ChevronRight className="w-4 h-4 text-navy/40" />
        </Link>
        <Link
          to="/assistente"
          className="flex items-center justify-between bg-white border border-border rounded-2xl px-5 py-4 hover:border-gold/40 transition-colors"
          data-testid="jornada-link-assistente"
        >
          <div className="flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-gold" />
            <p className="font-display text-base text-navy">Auxiliar Bíblico</p>
          </div>
          <ChevronRight className="w-4 h-4 text-navy/40" />
        </Link>
      </section>

      {!isAuthenticated && (
        <p className="text-[12px] text-navy/55 mt-6 text-center" data-testid="jornada-anon-note">
          Crie uma conta gratuita para sincronizar diário e ofensiva entre dispositivos.
        </p>
      )}

      {/* Indicar amigo, grow loop */}
      <section
        className="mt-8 bg-gradient-to-br from-[hsl(var(--parchment))] to-white border border-gold/40 rounded-3xl p-6 sm:p-7 text-center"
        data-testid="jornada-invite"
      >
        <BookOpen className="w-7 h-7 text-gold mx-auto mb-2" />
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Multiplique a Palavra</p>
        <h2 className="font-display text-2xl sm:text-3xl text-navy mt-2 leading-tight">
          Indique para um amigo
        </h2>
        <p className="text-sm text-navy/65 mt-2 max-w-sm mx-auto leading-relaxed">
          Compartilhe a graça que você está vivendo. Mande o app para alguém que precisa de uma palavra hoje.
        </p>
        <button
          type="button"
          onClick={() => setShareAppOpen(true)}
          data-testid="jornada-invite-cta"
          className="mt-5 inline-flex items-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy px-6 py-3 rounded-full text-[11px] uppercase tracking-widest transition-colors"
        >
          <Share2 className="w-3.5 h-3.5" />
          Indicar amigo
        </button>
      </section>

      {loading && (
        <p className="text-[11px] text-navy/45 mt-4 text-center">Atualizando dados…</p>
      )}

      <ShareStreakModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        days={stats.streak}
      />
      <ShareAppModal
        open={shareAppOpen}
        onClose={() => setShareAppOpen(false)}
      />
    </div>
  );
}
